import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { Textarea } from "@cmp-src/primitives/textarea";
import { cn } from "@gettactic/helpers";
import { ArrowUp, Globe, Lock, Paperclip, Reply } from "lucide-react";
import { useRef, useState } from "react";
import type { PendingAttachment } from "@/features/workplace-requests/hooks/usePendingAttachments";
import { CommentAttachmentsTray } from "./CommentAttachmentsTray";
import { AddCommentProps } from "./types";

type ComposerMode = "reply" | "internal";

// Internal notes aren't backed by the API yet (the comments endpoint has no
// visibility flag), so the tab is hidden for now. Flip to re-enable once the
// backend supports team-only notes.
const INTERNAL_NOTES_ENABLED = false;

interface RequestCommentComposerProps extends AddCommentProps {
  /** Name of the person the reply is addressed to (the request submitter). */
  recipientName?: string;
  /**
   * Hide the Reply / Internal note tabs and the "visible to" hint, leaving just
   * the composer. Used for guests, for whom internal notes don't apply.
   */
  simpleMode?: boolean;
  /** Files staged for this comment, in upload order. */
  attachments?: PendingAttachment[];
  onAddFiles?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  /** Blocks sending while bytes are still going up. */
  isUploadingAttachments?: boolean;
  /** How many more files may be attached; 0 disables the attach control. */
  remainingAttachmentSlots?: number;
}

/**
 * Fixed-footer comment composer. Renders the Reply / Internal note tabs, the
 * "visible to" hint, the staged attachments and the send control. The
 * internal-note tab is presentational only: the comments API has no visibility
 * flag, so both modes post a normal comment (see WorkplaceRequestComment).
 *
 * Attachments are optional: without `onAddFiles` the composer is text-only,
 * exactly as before.
 */
export const RequestCommentComposer = ({
  newComment,
  onCommentChange,
  onSubmit,
  isLoading,
  authenticatedUser,
  recipientName,
  simpleMode = false,
  attachments = [],
  onAddFiles,
  onRemoveAttachment,
  onRetryAttachment,
  isUploadingAttachments = false,
  remainingAttachmentSlots = 0
}: RequestCommentComposerProps) => {
  const [mode, setMode] = useState<ComposerMode>("reply");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isInternal =
    INTERNAL_NOTES_ENABLED && !simpleMode && mode === "internal";
  const canAttach = Boolean(onAddFiles);
  const hasReadyAttachment = attachments.some(
    (attachment) => attachment.status === "ready"
  );
  const canSubmit =
    (newComment.trim().length > 0 || hasReadyAttachment) &&
    !isLoading &&
    !isUploadingAttachments;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit();
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ""; // allow re-picking the same file
    if (files.length > 0) {
      onAddFiles?.(files);
    }
  };

  // Pasting a screenshot is the fastest way to attach one, and browsers put it
  // on the clipboard as a file rather than as text.
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach) return;
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    onAddFiles?.(files);
  };

  return (
    <div className="border-neutral-100 border-t bg-white px-8 py-3 max-sm:px-4">
      {simpleMode ? null : (
        <div className="mb-2 flex items-center justify-between">
          {INTERNAL_NOTES_ENABLED ? (
            <div className="flex items-center gap-1">
              <TabButton
                isActive={!isInternal}
                onClick={() => setMode("reply")}
                icon={<Reply className="h-3.5 w-3.5" />}
                label="Reply"
              />
              <TabButton
                isActive={isInternal}
                onClick={() => setMode("internal")}
                icon={<Lock className="h-3.5 w-3.5" />}
                label="Internal note"
              />
            </div>
          ) : null}
          <span className="flex items-center gap-1.5 font-inter text-neutral-400 text-xs">
            {isInternal ? (
              <>
                <Lock className="h-3 w-3" />
                Only visible to your team
              </>
            ) : (
              <>
                <Globe className="h-3 w-3" />
                {recipientName
                  ? `Visible to ${recipientName}`
                  : "Visible to requester"}
              </>
            )}
          </span>
        </div>
      )}

      <div className="flex items-end gap-2">
        <UserAvatar
          user={{
            name: authenticatedUser.user?.name || "",
            email: authenticatedUser.user?.email || "",
            profile_photo_url: authenticatedUser.user?.profile_photo_url || ""
          }}
          size="s"
          className="mb-1.5 flex-shrink-0"
        />
        <div
          className={cn(
            "flex min-h-[45px] flex-1 flex-col justify-center rounded-brand border px-3 shadow-xs transition-colors",
            isInternal
              ? "border-amber-200 bg-amber-50/60"
              : "border-neutral-200 bg-white focus-within:border-neutral-300"
          )}
        >
          {canAttach && onRemoveAttachment ? (
            <CommentAttachmentsTray
              attachments={attachments}
              onRemove={onRemoveAttachment}
              onRetry={onRetryAttachment}
            />
          ) : null}

          <div className="flex items-end gap-1">
            <Textarea
              placeholder={
                isInternal
                  ? "Add an internal note…"
                  : recipientName
                    ? `Reply to ${recipientName}…`
                    : "Add a comment…"
              }
              value={newComment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onCommentChange(e.target.value)
              }
              onPaste={handlePaste}
              className="max-h-40 min-h-0 w-full flex-1 resize-none border-0 bg-transparent px-0 py-1.5 shadow-none focus-visible:ring-0"
              rows={1}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />

            {canAttach ? (
              <>
                <IconButton
                  icon={Paperclip}
                  size="sm"
                  variant="ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || remainingAttachmentSlots === 0}
                  aria-label="Attach files"
                  title={
                    remainingAttachmentSlots === 0
                      ? "Attachment limit reached"
                      : "Attach files"
                  }
                  className="mb-0.5 shrink-0 text-neutral-400 hover:text-neutral-700"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFilesSelected}
                />
              </>
            ) : null}
          </div>
        </div>
        <IconButton
          icon={ArrowUp}
          size="md"
          variant="ghost"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label="Send"
          className={cn(
            "h-[45px] w-[45px] shrink-0 rounded-brand transition-colors disabled:opacity-100",
            canSubmit
              ? "bg-neutral-900 text-white hover:bg-neutral-800"
              : "bg-neutral-200 text-neutral-400"
          )}
        />
      </div>
    </div>
  );
};

const TabButton = ({
  isActive,
  onClick,
  icon,
  label
}: {
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-inter font-medium text-xs transition-colors",
      isActive
        ? "bg-neutral-100 text-neutral-900"
        : "text-neutral-400 hover:text-neutral-600"
    )}
  >
    {icon}
    {label}
  </button>
);
